/**
 * "Signed in to a different portal" (#420 host binding). The API answers 401 `wrong_portal` when an
 * ordinary tenant session is used on another tenant's portal address, and clears this host's cookie.
 * The axios interceptor notes it here; the sign-in page then explains what happened instead of the
 * generic "session expired", and offers the person's own portal when this browser already knows it.
 *
 * "Known" means only what this page already had: the cached user's tenant and the tenant list
 * /auth/me returned earlier on this address. The API's 401 deliberately says nothing about which
 * portal the session belongs to (a copied cookie shouldn't learn where it works).
 */
import type { AvailableTenant } from '../types';
import { PORTAL_HOST_SUFFIX } from '../utils/portalDomain';

export const WRONG_PORTAL_CODE = 'wrong_portal';
export const WRONG_PORTAL_EVENT = 'auth:wrong-portal';
const KEY = 'bcp_wrong_portal';

export interface WrongPortalNotice {
  /** Sign-in address of the person's own portal, when this browser knows it. */
  homeUrl: string | null;
  homeName: string | null;
}

/** True for an axios-style error carrying the API's 401 `wrong_portal`. */
export function isWrongPortalError(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
  return res?.status === 401 && (res.data as { code?: unknown } | null | undefined)?.code === WRONG_PORTAL_CODE;
}

const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

/** The sign-in address of another tenant's portal on the same parent domain, or null. */
export function portalLoginUrl(subdomain: string, loc: Pick<Location, 'protocol' | 'hostname' | 'port'> = window.location): string | null {
  if (!SUBDOMAIN_RE.test(subdomain)) { return null; }
  const host = loc.hostname.toLowerCase();
  for (const suffix of [PORTAL_HOST_SUFFIX, '.localhost']) {
    if (host.endsWith(suffix)) {
      const current = host.slice(0, -suffix.length);
      if (!current || current === subdomain.toLowerCase()) { return null; }
      const port = loc.port ? `:${loc.port}` : '';
      return `${loc.protocol}//${subdomain.toLowerCase()}${suffix}${port}/login`;
    }
  }
  return null;
}

function readJson<T>(key: string): T | null {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) as T : null; } catch { return null; }
}

/** The person's own portal, from what this page cached before the 401 (nothing is fetched). */
function knownHomePortal(loc: Pick<Location, 'protocol' | 'hostname' | 'port'>): Pick<WrongPortalNotice, 'homeUrl' | 'homeName'> {
  const tenantId = readJson<{ tenantId?: string }>('bc-portal-user')?.tenantId;
  const tenants = readJson<AvailableTenant[]>('bc-portal-available-tenants');
  if (!tenantId || !Array.isArray(tenants)) { return { homeUrl: null, homeName: null }; }
  const home = tenants.find((t) => t && t.id === tenantId && typeof t.subdomain === 'string');
  const homeUrl = home ? portalLoginUrl(home.subdomain as string, loc) : null;
  return { homeUrl, homeName: homeUrl && typeof home?.name === 'string' && home.name ? home.name : null };
}

/** Called by the axios interceptor on a 401 wrong_portal (before the app clears its cache). */
export function noteWrongPortal(loc: Pick<Location, 'protocol' | 'hostname' | 'port'> = window.location): void {
  const notice: WrongPortalNotice = knownHomePortal(loc);
  try { sessionStorage.setItem(KEY, JSON.stringify(notice)); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(WRONG_PORTAL_EVENT, { detail: notice })); } catch { /* no window */ }
}

/** One-shot: the notice saved by noteWrongPortal, or null. Reading it clears it. */
export function takeWrongPortalNotice(): WrongPortalNotice | null {
  try {
    const v = sessionStorage.getItem(KEY);
    if (v === null) { return null; }
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(v) as Partial<WrongPortalNotice> | null;
    return { homeUrl: typeof parsed?.homeUrl === 'string' ? parsed.homeUrl : null, homeName: typeof parsed?.homeName === 'string' ? parsed.homeName : null };
  } catch { return null; }
}
